import { Namespace, Ref } from '@repo/sdk/models/components';

import { SubscriptionIdentifier } from '@/hooks/query-hooks/gaia-api/use-subscription-base';

/**
 * Maps reading references to subscription identifiers.
 *
 * Filters references to ensure they are readings with valid IDs and transforms
 * them into subscription identifiers with the specified context key.
 *
 * @param readingRefs - Array of reading references
 * @param contextKey - Optional context key to categorize the readings (e.g., 'displayMetrics', 'componentReadings')
 * @returns Array of subscription identifiers
 */
export const mapReadingRefsToSubscriptionIdentifiers = (
  readingRefs: Ref[],
  contextKey?: string
): SubscriptionIdentifier[] => {
  const ids =
    readingRefs
      ?.filter((ref) => ref.namespace !== Namespace.Setting && ref.id)
      ?.map((ref) => ref.id)
      ?.filter((id): id is string => !!id) ?? [];
  // Deduplicate by id
  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.map((id) => ({ id, contextKey: contextKey ?? 'default' }));
};

/**
 * Maps setting references to subscription identifiers.
 *
 * Filters references to ensure they are settings with valid IDs and transforms
 * them into subscription identifiers with the specified context key.
 *
 * @param settingRefs - Array of setting references
 * @param contextKey - Optional context key to categorize the settings (e.g., 'displaySettings', 'componentSettings')
 * @returns Array of subscription identifiers
 */
export const mapSettingRefsToSubscriptionIdentifiers = (
  settingRefs: Ref[],
  contextKey?: string
): SubscriptionIdentifier[] => {
  const ids =
    settingRefs
      ?.filter((ref) => ref.namespace === Namespace.Setting && ref.id)
      ?.map((ref) => ref.id)
      ?.filter((id): id is string => !!id) ?? [];
  // Deduplicate by id
  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.map((id) => ({ id, contextKey: contextKey ?? 'default' }));
};
